import { createContext, ReactNode, useCallback, useMemo, useState } from 'react';
import { defaultCoordinates } from '~/components/map/constants';
import { AgentCluster } from '~/sections/agents/search-results/map-view/agents-sidebar/agent-card/types';

type FilterOption = { name: string; count: number };

type MapContextProps = {
  coordinates: Coordinates;
  types: string[];
  sidebarReopen: boolean;
  sidebarAgents: AgentCluster[];
  sidebarCompanies: FilterOption[];
  sidebarStates: FilterOption[];
  sidebarTotalCount: number;
  zoomLevel: number | null;
  mapActions: {
    changeCoordinates: (params: Coordinates, zoomLevel?: number) => void;
    changeTypes: (params: string[]) => void;
    changeSidbarReopen: (params: boolean) => void;
    changeSidebarAgents: (params: AgentCluster[]) => void;
    changeSidebarCompanies: (params: FilterOption[]) => void;
    changeSidebarStates: (params: FilterOption[]) => void;
    changeSidebarTotalCount: (params: number) => void;
    changeZoomLevel: (zoomLevel: number | null) => void;
  };
};

const defaultTypes: string[] = [];
const defaultZoomLevel = null;

const initialState: MapContextProps = {
  coordinates: defaultCoordinates,
  types: defaultTypes,
  sidebarReopen: false,
  sidebarAgents: [],
  sidebarCompanies: [],
  sidebarStates: [],
  sidebarTotalCount: 0,
  zoomLevel: defaultZoomLevel,
  mapActions: {
    changeCoordinates: () => {},
    changeTypes: () => {},
    changeSidbarReopen: () => {},
    changeSidebarAgents: () => {},
    changeSidebarCompanies: () => {},
    changeSidebarStates: () => {},
    changeSidebarTotalCount: () => {},
    changeZoomLevel: () => {},
  },
};

const AgentsMapContext = createContext(initialState);

type MapProviderProps = {
  children: ReactNode;
};

function AgentsMapProvider({ children }: MapProviderProps) {
  const [coordinates, setCoordinates] = useState(defaultCoordinates);
  const changeCoordinates = useCallback((coordinates: Coordinates, zoomLevel?: number) => {
    if (zoomLevel) {
      setZoomLevel(zoomLevel);
    }
    setCoordinates(coordinates);
  }, []);

  const [types, setTypes] = useState(defaultTypes);
  const changeTypes = useCallback((types: string[]) => {
    setTypes(types);
  }, []);

  const [sidebarReopen, setSidebarReopen] = useState(false);
  const changeSidbarReopen = useCallback((sidebarReopen: boolean) => {
    setSidebarReopen(sidebarReopen);
  }, []);

  const [sidebarAgents, setSidebarLoanOfficers] = useState<AgentCluster[]>([]);
  const changeSidebarAgents = useCallback((sidebarAgents: AgentCluster[]) => {
    setSidebarLoanOfficers(sidebarAgents);
  }, []);

  const [sidebarCompanies, setSidebarCompanies] = useState<FilterOption[]>([]);
  const changeSidebarCompanies = useCallback((sidebarCompanies: FilterOption[]) => {
    setSidebarCompanies(sidebarCompanies);
  }, []);

  const [sidebarStates, setSidebarStates] = useState<FilterOption[]>([]);
  const changeSidebarStates = useCallback((sidebarStates: FilterOption[]) => {
    setSidebarStates(sidebarStates);
  }, []);

  const [sidebarTotalCount, setSidebarTotalCount] = useState(0);
  const changeSidebarTotalCount = useCallback((sidebarTotalCount: number) => {
    setSidebarTotalCount(sidebarTotalCount);
  }, []);
  const [zoomLevel, setZoomLevel] = useState<number | null>(defaultZoomLevel);
  const changeZoomLevel = (newZoomLevel: number | null) => {
    setZoomLevel(newZoomLevel);
  };

  const mapActions = useMemo(
    () => ({
      changeTypes,
      changeCoordinates,
      changeSidbarReopen,
      changeSidebarAgents,
      changeSidebarCompanies,
      changeSidebarStates,
      changeSidebarTotalCount,
      changeZoomLevel,
    }),
    [
      changeTypes,
      changeCoordinates,
      changeSidbarReopen,
      changeSidebarAgents,
      changeSidebarCompanies,
      changeSidebarStates,
      changeSidebarTotalCount,
    ]
  );

  return (
    <AgentsMapContext.Provider
      value={{
        coordinates,
        types,
        sidebarReopen,
        sidebarAgents,
        sidebarCompanies,
        sidebarStates,
        sidebarTotalCount,
        zoomLevel,
        mapActions,
      }}
    >
      {children}
    </AgentsMapContext.Provider>
  );
}

export { AgentsMapProvider, AgentsMapContext };
